import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fetch as fetchCompanyUsers } from "services/user-select.service";
import { createAssetRequest } from "services/asset-requests.service";
import DialogShell from "./DialogShell";
import OptionPicker, { PickerOption } from "./OptionPicker";
import { describeAssetRequestError, MAX_TEXT_LENGTH } from "./asset-requests-helpers";

type Props = {
  companyId: string;
  /** Set when an employee asks for themselves: no employee list, and the comment is required. */
  selfUserId?: string;
  onClose: () => void;
  /** Called after the request is saved; the caller closes the dialog and reloads. */
  onCreated: () => void;
};

export default function NewRequestDialog({ companyId, selfUserId, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const [employeeId, setEmployeeId] = useState(selfUserId || "");
  const [comment, setComment] = useState("");
  const [employees, setEmployees] = useState<PickerOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(!selfUserId);

  useEffect(() => {
    if (selfUserId) return undefined;
    let cancelled = false;
    // Employees only: guests and authorized persons (isPrivate) would be refused by the server.
    fetchCompanyUsers(companyId, {}, false, false, true)
      .then((data: { usersCollection: { id: string; name?: string; email?: string }[] }) => {
        if (cancelled) return;
        setEmployees(
          data.usersCollection.map((user) => ({
            id: user.id,
            label: user.name || user.email || user.id,
            detail: user.name ? user.email : undefined,
          }))
        );
      })
      .catch(() => {
        if (!cancelled) toast.error(t("asset_requests_error_generic"));
      })
      .finally(() => {
        if (!cancelled) setLoadingEmployees(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, selfUserId, t]);

  const create = async () => {
    try {
      await createAssetRequest(companyId, { employeeId, comment: comment.trim() });
      toast.success(t("asset_requests_created"));
      onCreated();
    } catch (e) {
      toast.error(describeAssetRequestError(e, t));
    }
  };

  const commentIsRequired = Boolean(selfUserId);
  return (
    <DialogShell
      title={selfUserId ? t("asset_requests_self_title") : t("asset_requests_new")}
      submitLabel={t("asset_requests_create")}
      submitDisabled={!employeeId || (commentIsRequired && !comment.trim())}
      onSubmit={create}
      onClose={onClose}
    >
      {selfUserId ? (
        <p className="text-sm text-gray-600">{t("asset_requests_self_hint")}</p>
      ) : (
        <div>
          <p className="mb-1 text-sm font-medium text-gray-700">{t("employee")}</p>
          <OptionPicker
            options={employees}
            selectedId={employeeId}
            onSelect={setEmployeeId}
            searchLabel={t("asset_requests_search_employee")}
            emptyLabel={t("asset_requests_no_employee")}
            loading={loadingEmployees}
          />
        </div>
      )}
      <label className="block text-sm font-medium text-gray-700">
        {commentIsRequired ? `${t("comment")} *` : t("comment")}
        <textarea
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          maxLength={MAX_TEXT_LENGTH}
          rows={4}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-base font-normal md:text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </label>
    </DialogShell>
  );
}
